import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import type { Op } from 'quill/core';
import React, { useEffect, useState } from 'react';
import * as styles from './Annotation.module.scss';
import AnnotationCreate from './AnnotationCreate';
import AnnotationIcon from './AnnotationIcon';
import AnnotationLink from './AnnotationLink';
import AnnotationPreview from './AnnotationPreview';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import PopOver from '../../components/deprecated/PopOver';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import OpacityMotionTransition from '../../components/OpacityMotionTransition';
import Tooltip from '../../components/Tooltip';
import { CREATION_ID, EditorActions } from '../../editor/editorSlice';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import editIcon from '../../icons/non-prefilled/16x16/editPost.svg';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import { UIActions } from '../../ui/uiSlice';
import addTestId from '../../utils/addTestId';
import { usePostLayoutSize } from '../../utils/responsive/PostLayoutSizeProvider';
import useRemoveHash from '../../utils/router/useRemoveHash';
import annotationThreadHash from '../../utils/url/annotationThreadHash';
import fullOrgUrl from '../../utils/url/fullOrgUrl';
import postPath from '../../utils/url/postPath';
import { sanitizeAnnotationLink } from '../../utils/url/sanitizeUrl';
import type { CompactPopOverSide } from '../comment/getMagneticWallItemLayout';
import MagneticWallItemOverflowMenu, {
  MagneticWallOverflowButton,
} from '../comment/MagneticWallItemOverflowMenu';
import showFullPageSheet from '../comment/showFullPageSheet';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallItem from '../MagneticWallItem';
import { useOptimisticItemContentRects } from '../useOptimisticItemContentRects';
import useShareSecret from '../useShareSecret';
import isContentEmpty from '../utils/isContentEmpty';

const POST_FRAGMENT = gql(`
  fragment Annotation_post on RootPostFields {
    id
    ...postPath_post
  }
`);

const ANNOTATION_FRAGMENT = gql(`
  fragment Annotation_annotation on Annotation {
    id
    linkText
    ...AnnotationPreview_annotation
  }
`);

interface AnnotationCompactProps {
  onClick: () => void;
  active: boolean;
}

const AnnotationCompact = ({ onClick, active }: AnnotationCompactProps) => {
  const postLayoutSize = usePostLayoutSize();

  const isTabletIcon = postLayoutSize === 'tablet';

  return (
    <div
      {...addTestId('annotation-icon')}
      className={classNames({
        [styles.compactAnnotationIconContainer]: !isTabletIcon,
        [styles.ring]: !isTabletIcon && active && showFullPageSheet(),
      })}
      onClick={onClick}
    >
      {isTabletIcon ? (
        <AnnotationIcon size="small" />
      ) : (
        <AnnotationIcon hoverable={!active} />
      )}
    </div>
  );
};

interface AnnotationFullIconContainerProps {
  children: React.ReactNode;
  onClick: () => void;
}

const AnnotationFullIconContainer = ({
  children,
  onClick,
}: AnnotationFullIconContainerProps) => {
  return (
    <div className={styles.fullIconContainer}>
      <div
        className={styles.icon}
        {...addTestId('annotation-icon')}
        onClick={onClick}
      >
        {children}
      </div>
    </div>
  );
};

interface AnnotationPreviewContainerProps {
  children: React.ReactNode;
  onClick?: () => void;
}

export const AnnotationPreviewContainer = ({
  children,
  onClick,
}: AnnotationPreviewContainerProps) => {
  return (
    <div className={styles.previewContainer}>
      <div onClick={onClick} className={styles.preview}>
        {children}
      </div>
    </div>
  );
};

interface AnnotationProps {
  active: boolean;
  annotation: DocumentType<typeof ANNOTATION_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  onResize?: () => void;
  unsavedInputValue?: Op[];
  onInputChange: (content: Op[]) => void;
  popOverOpen: boolean;
  overlapped: boolean;
  size: MagneticWallItemSize;
  canEdit: boolean;
  compactPopOverSide: CompactPopOverSide;
  createAnnotation: React.ComponentProps<
    typeof AnnotationCreate
  >['createAnnotation'];
  deleteAnnotation: () => void;
  onPopOverHeightChange: (height: number) => void;
}

const Annotation = ({
  active,
  annotation,
  post,
  onResize,
  unsavedInputValue = [],
  onInputChange,
  popOverOpen,
  overlapped,
  size,
  canEdit,
  compactPopOverSide,
  createAnnotation,
  deleteAnnotation,
  onPopOverHeightChange,
}: AnnotationProps) => {
  const shareSecret = useShareSecret();
  const isNewAnnotation = annotation.id === CREATION_ID;
  const dispatch = useAppDispatch();

  const [linkText, setLinkText] = useState(annotation.linkText ?? '');
  const trimmedLinkText = linkText.trim();
  const [link, setLink] = useState(annotation.link ?? '');
  const sanitizedLink = sanitizeAnnotationLink(link);

  const [isEditing, setIsEditing] = useState(isNewAnnotation);
  const [isHovered, setIsHovered] = useState(false);
  const optimisticItemContentRects = useOptimisticItemContentRects();

  useEffect(() => {
    onResize?.();

    if (!active) {
      setIsEditing(false);
    }
  }, [onResize, active]);

  useEffect(() => {
    if (isEditing) {
      setLinkText(annotation.linkText ?? '');
      setLink(annotation.link ?? '');
    }
  }, [isEditing, annotation.linkText, annotation.link]);

  const onDismiss = () => {
    dispatch(EditorActions.blurMagneticWall());
  };

  const onCancel = () => {
    setLinkText(annotation.linkText ?? '');
    setLink(annotation.link ?? '');
    setIsEditing(false);
  };

  const focusAnnotationById = (id: string) => {
    dispatch(
      EditorActions.focusMagneticWall({
        id,
        type: 'annotation',
        isActive: true,
        hasExpandIntent: false,
      }),
    );
  };

  const focusCurrentAnnotation = () => focusAnnotationById(annotation.id);

  const removeHash = useRemoveHash();

  const onDelete = () => {
    deleteAnnotation();

    if (window.location.hash === annotationThreadHash(annotation.id)) {
      removeHash();
    }
    onDismiss();
  };

  const shouldShowLink =
    (isEditing || !isContentEmpty(annotation.content)) && sanitizedLink;

  const innerFullView = (
    <div className={styles.innerFullViewContainer}>
      {isEditing ? (
        <div className={styles.createContainer}>
          <AnnotationCreate
            annotationId={annotation.id}
            initialContent={
              isNewAnnotation
                ? unsavedInputValue
                : (annotation.content ?? unsavedInputValue)
            }
            onChange={onInputChange}
            onSaved={() => {
              onInputChange?.([]);
              setIsEditing(false);
            }}
            link={link}
            onChangeLink={setLink}
            linkText={linkText}
            onChangeLinkText={setLinkText}
            onCancel={onCancel}
            createAnnotation={createAnnotation}
          />
        </div>
      ) : (
        <AnnotationPreviewContainer onClick={focusCurrentAnnotation}>
          <AnnotationPreview
            annotation={annotation}
            avatar={<AnnotationIcon />}
          />
        </AnnotationPreviewContainer>
      )}
      {isHovered &&
        !isEditing &&
        !optimisticItemContentRects.has(annotation.id) && (
          <MagneticWallItemOverflowMenu
            menuItems={
              canEdit
                ? [
                    <MenuItem
                      icon={editIcon}
                      key="edit"
                      onClick={() => {
                        focusCurrentAnnotation();
                        setIsEditing(true);
                      }}
                      testId="edit-annotation-button"
                    >
                      Edit annotation
                    </MenuItem>,
                    <MenuSeparator key="separator" />,
                    <MenuItem
                      icon={trashIcon}
                      key="delete"
                      danger
                      onClick={onDelete}
                    >
                      Delete annotation
                    </MenuItem>,
                  ]
                : []
            }
          >
            <Tooltip content={`Copy link\nto annotation`}>
              <MagneticWallOverflowButton
                onClick={() => {
                  const url = fullOrgUrl(
                    postPath(post, {
                      shareSecret,
                      anchor: annotationThreadHash(annotation.id),
                    }),
                  );

                  navigator.clipboard.writeText(url).then(() => {
                    dispatch(
                      UIActions.pushToast({
                        level: 'success',
                        message: 'Link copied to clipboard',
                      }),
                    );
                  });
                }}
                icon={linkIcon}
              />
            </Tooltip>
          </MagneticWallItemOverflowMenu>
        )}
    </div>
  );

  return (
    <MagneticWallItem.Root
      focused={active}
      onMouseEnter={() => {
        dispatch(EditorActions.highlightAnnotation(annotation.id));
      }}
      onMouseLeave={() => {
        dispatch(EditorActions.removeMagneticWallHighlight());
      }}
      id={`annotation-${annotation.id}`}
      fullView={
        <MagneticWallItem.Card
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          focused={active}
          overlapped={overlapped}
        >
          <AnimatePresence>
            {active ? (
              <>{innerFullView}</>
            ) : (
              <AnnotationFullIconContainer
                onClick={() => focusCurrentAnnotation()}
              >
                <AnnotationIcon hoverable />
              </AnnotationFullIconContainer>
            )}
            {active && shouldShowLink && (
              <OpacityMotionTransition
                key="link"
                disabled={isEditing}
                transition={{ duration: 0.25 }}
              >
                <AnnotationLink link={sanitizedLink} text={trimmedLinkText} />
              </OpacityMotionTransition>
            )}
          </AnimatePresence>
        </MagneticWallItem.Card>
      }
      compactView={
        <MagneticWallItem.ResponsivePopover
          onClose={onDismiss}
          target={
            <AnnotationCompact
              active={active}
              onClick={() => focusCurrentAnnotation()}
            />
          }
          onPopOverHeightChange={onPopOverHeightChange}
          content={
            <PopOver.Container noPadding>
              <div
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                {innerFullView}
                {shouldShowLink && (
                  <div className={styles.annotationLinkContainer}>
                    <AnnotationLink
                      link={sanitizedLink}
                      text={trimmedLinkText}
                    />
                  </div>
                )}
              </div>
            </PopOver.Container>
          }
          compactPopOverSide={compactPopOverSide}
          open={popOverOpen}
        />
      }
      size={size}
    />
  );
};

export default Annotation;
